import validator from "rpu-validator"
import { FORM_ERROR, ValidationErrors } from "final-form"

type Address = {
  streetAddress1: Maybe<string>
  streetAddress2: Maybe<string>
  state: Maybe<string>
  city: Maybe<string>
  zipCode: Maybe<string>
}

const validateAddress = async (address: Address): Promise<ValidationErrors> => {
  const state = address.state || ""
  let errors = {}

  if (isMilitaryState(state)) {
    errors = validateMilitaryAddress(address)
  } else {
    errors = await validateRegularAddress(address)
  }
  return errors
}

const validateRegularAddress = async (address: Address): Promise<ValidationErrors> => {
  const state = address.state || ""
  const city = address.city || ""
  const zipCode = address.zipCode || ""

  let validAddress,
    validZip: any = null

  try {
    validZip = await validator.zip("287DCONS5906", zipCode.trim())
    validAddress = await fetchValidAddress(address)

    if (validZip.state.toUpperCase() !== state.toUpperCase()) {
      return {
        state: "State doesn't match zip code provided"
      }
    }

    if (validAddress.city.trim().toUpperCase() !== city.trim().toUpperCase()) {
      return {
        city: "City doesn’t match zip code provided"
      }
    }
  } catch (e) {
    let error = e.error as string | Error

    /** Some errors (e.g. network errors) are Error objects */
    if (typeof error !== "string") {
      console.error(e)
      return { [FORM_ERROR]: error.message || "Unknown Error" }
    }

    if (error.includes("Zip") || error.includes("ZIPCode")) {
      error = "Invalid zip code"

      return { zipCode: error }
    } else if (error.includes("City")) {
      return { city: error }
    } else {
      return { streetAddress1: error }
    }
  }

  return {}
}

const validateMilitaryAddress = (address: Address): ValidationErrors => {
  const city = address.city || ""

  if (isMilitaryCity(city)) {
    return {}
  }

  return {
    city: "City can only contain APO/FPO/DPO."
  }
}

const isMilitaryState = (state: string): boolean => {
  if (!state) {
    return false
  }

  return ["AA", "AE", "AP"].includes(state)
}

const isMilitaryCity = (city: string): boolean => {
  if (!city) {
    return false
  }

  return !!["APO", "FPO", "DPO"].find(militaryCity => militaryCity === city.trim().toUpperCase())
}

const fetchValidAddress = async (address: Address) => {
  const streetAddress1 = address.streetAddress1 || ""
  const state = address.state || ""
  const city = address.city || ""
  const zipCode = address.zipCode || ""

  return validator.address("287DCONS5906", {
    street: removeSpecialCharacters(streetAddress1),
    city: city,
    state: state,
    zip: zipCode.trim()
  })
}

const removeSpecialCharacters = (inputString: string) => inputString.replace(/#/gi, "")

export default validateAddress
